/**
 * Report delivery.
 *
 * Three ways out of the workbench, because a report has three audiences:
 *
 *  - HTML file — the durable artefact. Attach it to a ticket, commit it, mail
 *    it. Self-contained, no network, opens anywhere.
 *  - PDF — what gets read. Generated by handing the document to the host's own
 *    print pipeline, which is the only route that produces real vector text
 *    with selectable, searchable, correctly hinted glyphs. Every alternative
 *    available in a webview either rasterises the page (a screenshot in a
 *    trench coat — unsearchable, blurry, enormous) or needs a headless
 *    Chromium download the app does not ship.
 *  - Browser — hands the file to the default browser so the reader gets the
 *    browser's own print dialog, with its margin and header controls.
 *
 * The PDF path deliberately does not claim to be silent. Tauri's webview
 * exposes no programmatic print-to-file, and pretending otherwise with a
 * canvas rasteriser would trade a real PDF for a worse one. What it does
 * guarantee is that the document is already laid out for paper when the
 * dialog opens: page size, margins, running footers, page numbers, and break
 * rules are all in the template's print stylesheet.
 */

import { save as saveDialog } from "@tauri-apps/plugin-dialog";
import { open as openWithSystem } from "@tauri-apps/plugin-shell";
import { writeTextFile } from "@tauri-apps/plugin-fs";

import reportTemplate from "../ui/report.html?raw";

/**
 * The script element the payload is injected into.
 *
 * This exact string is the contract between the exporter and the template. The
 * template is imported at build time with `?raw` rather than fetched at
 * runtime: fetching `/src/ui/report.html` worked in the dev server and 404'd in
 * the packaged application, which meant export was broken in every shipped
 * build — the kind of defect that only surfaces after a release.
 */
const PAYLOAD_ANCHOR = '<script id="scan-data" type="application/json"></script>';

/**
 * Renders the self-contained document.
 *
 * `<` is escaped inside the JSON payload so that a `</script>` occurring in a
 * finding description — source code is full of them — cannot terminate the
 * script element early. Without this, a single finding containing a closing
 * script tag produces a report that silently loses every finding after it.
 */
export function renderReport(payload: unknown): string {
  if (!reportTemplate.includes(PAYLOAD_ANCHOR)) {
    throw new Error(
      "EcoTrace report template is missing its payload anchor; the template and the exporter have drifted apart."
    );
  }

  const json = JSON.stringify(payload).replace(/</g, "\\u003c");
  return reportTemplate.replace(
    PAYLOAD_ANCHOR,
    `<script id="scan-data" type="application/json">${json}</script>`
  );
}

export type ReportFormat = "html" | "pdf";

export interface WriteReportOptions {
  html: string;
  format: ReportFormat;
  /** Basename without extension, e.g. `ecotrace-report-2026-09-26`. */
  suggestedName: string;
  /** Skips the save dialog and returns the chosen path. */
  target?: string;
}

/**
 * Writes the report and opens it in the way the format calls for.
 *
 * Returns the path written, or null when the user cancelled. In a browser —
 * that is, in the hosted demo — there is no filesystem and no save dialog, so
 * the document is delivered as a download instead and null is returned only if
 * the download itself is unavailable.
 *
 * `format: "pdf"` is refused rather than honoured: the report body this
 * function writes is always HTML, so accepting a .pdf path would put markup
 * in a file the reader's PDF viewer cannot open — worse than no file at all.
 * PDF has exactly one route out of here, the host print pipeline in
 * `printToPdf`, and it is the caller's job to take it.
 */
export async function writeReport(
  options: WriteReportOptions
): Promise<string | null> {
  if (options.format === "pdf") {
    throw new Error(
      "EcoTrace writes HTML, not PDF bytes; export PDF through printToPdf() instead of writeReport({ format: \"pdf\" })."
    );
  }

  if (!("__TAURI_INTERNALS__" in window)) {
    return downloadInBrowser(options.html, `${options.suggestedName}.html`);
  }

  const target =
    options.target ??
    (await saveDialog({
      title: "Export EcoTrace report as HTML",
      defaultPath: `${options.suggestedName}.html`,
      filters: [
        {
          name: "HTML report",
          extensions: ["html"],
        },
      ],
    }));

  if (!target) return null;

  await writeTextFile(target, options.html);
  return target;
}

/**
 * Delivers a string as a file download.
 *
 * The demo has no filesystem, and the report is worthless if it cannot leave
 * the browser, so the hosted build uses an object URL and a synthetic click.
 * The URL is revoked on the next tick rather than immediately: revoking it
 * synchronously can cancel the download before the browser has read the blob.
 */
function downloadInBrowser(html: string, filename: string): string | null {
  try {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    return filename;
  } catch {
    return null;
  }
}

/**
 * Prints the document through the host print pipeline.
 *
 * The document is loaded into a hidden iframe rather than printed from the
 * workbench window: the workbench window carries the app chrome, and printing
 * it would produce a PDF of the wrong document. The iframe is created, sized
 * off-screen but *not* `display:none` — a zero-sized or undisplayed frame has
 * no layout, and a document with no layout prints blank.
 *
 * Removal is deferred until after `afterprint` (or a timeout), because tearing
 * the frame down while the print dialog is still rasterising it produces an
 * empty or truncated PDF.
 */
export async function printToPdf(html: string): Promise<void> {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("title", "EcoTrace print preview");
  frame.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    "width:794px", // A4 at 96dpi
    "height:1123px",
    "border:0",
    "visibility:hidden",
    "pointer-events:none",
  ].join(";");

  document.body.appendChild(frame);

  const cleanup = (): void => {
    window.removeEventListener("afterprint", onAfterPrint);
    frame.remove();
  };
  const onAfterPrint = (): void => {
    // Give the print pipeline a beat to finish before the frame disappears.
    window.setTimeout(cleanup, 1000);
  };
  window.addEventListener("afterprint", onAfterPrint, { once: true });

  const doc = frame.contentDocument;
  if (!doc) {
    cleanup();
    throw new Error("Could not open a document frame to print.");
  }

  doc.open();
  doc.write(html);
  doc.close();

  await waitForDocumentReady(doc);
  await waitForFonts(doc);

  // The dialog is modal and blocking in WebView2; print() returns once it is
  // dismissed, so the caller's progress state resolves at the right moment.
  frame.contentWindow?.focus();
  frame.contentWindow?.print();

  // If the dialog was cancelled or never opened, do not leak the frame.
  window.setTimeout(cleanup, 120_000);
}

function waitForDocumentReady(doc: Document): Promise<void> {
  if (doc.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    doc.addEventListener("readystatechange", () => {
      if (doc.readyState === "complete") resolve();
    });
    // Never hang the export on a document that refuses to signal readiness.
    window.setTimeout(resolve, 3000);
  });
}

/**
 * Waits for the embedded IBM Plex faces to load.
 *
 * Printing a document whose @font-face has not resolved produces a PDF in a
 * fallback face — visually wrong, and the substitution is not obvious in the
 * print preview, so it tends to reach the reader. `document.fonts.ready`
 * settles once pending face loads finish.
 */
async function waitForFonts(doc: Document): Promise<void> {
  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  try {
    await Promise.race([
      fonts.ready,
      new Promise((resolve) => window.setTimeout(resolve, 2000)),
    ]);
  } catch {
    // A font-loading failure must not block the export.
  }
}

/**
 * Hands a written file to the operating system's default handler.
 *
 * Failures are swallowed: not being able to launch a default application is not
 * a failure of the export, which has already written the file by this point.
 */
export async function revealReport(path: string): Promise<void> {
  if (!("__TAURI_INTERNALS__" in window)) {
    // Already downloaded by `writeReport`; nothing to hand off to the OS.
    return;
  }
  try {
    await openWithSystem(path);
  } catch {
    // The file is on disk. That was the contract.
  }
}
